import Transaction from "../../entities/transaction";

/**
 * Transaction actions
 *
 * Call order:
 *
 * TRANSACTION_SAVE_QUEUE:	adds the item to the save queue
 * --> TRANSACTION_SAVE:	modifies the state
 * --> TRANSACTION_SAVE_SUCCESS: if all went well
 * --> TRANSACTION_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail TRANSACTION_SAVE_QUEUE is during the call to TRANSACTION_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at TRANSACTION_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: TRANSACTION_SAVE_SUCCESS, TRANSACTION_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or TRANSACTION_PENDING_CHECK (it left the queue).
 */

export const TRANSACTION_FETCH_ONE_QUEUE: any = 'TRANSACTION_FETCH_ONE_QUEUE';
export const TRANSACTION_FETCH_SUCCESS: any = 'TRANSACTION_FETCH_SUCCESS';
export const TRANSACTION_FETCH_FAILURE: any = 'TRANSACTION_FETCH_FAILURE';
export const TRANSACTION_FETCH_ONE_SUCCESS: any = 'TRANSACTION_FETCH_ONE_SUCCESS';
export const TRANSACTION_FETCH_ONE_FAILURE: any = 'TRANSACTION_FETCH_ONE_FAILURE';

export const TRANSACTION_SAVE: any = 'TRANSACTION_SAVE';
export const TRANSACTION_SAVE_QUEUE: any = 'TRANSACTION_SAVE_QUEUE';
export const TRANSACTION_SAVE_SUCCESS: any = 'TRANSACTION_SAVE_SUCCESS';
export const TRANSACTION_SAVE_FAILURE: any = 'TRANSACTION_SAVE_FAILURE';

export const TRANSACTION_SAVE_MANY: any = 'TRANSACTION_SAVE_MANY';
export const TRANSACTION_SAVE_MANY_QUEUE: any = 'TRANSACTION_SAVE_MANY_QUEUE';
export const TRANSACTION_SAVE_MANY_SUCCESS: any = 'TRANSACTION_SAVE_MANY_SUCCESS';
export const TRANSACTION_SAVE_MANY_FAILURE: any = 'TRANSACTION_SAVE_MANY_FAILURE';

export const TRANSACTION_REMOVE: any = 'TRANSACTION_REMOVE';
export const TRANSACTION_REMOVE_QUEUE: any = 'TRANSACTION_REMOVE_QUEUE';
export const TRANSACTION_REMOVE_SUCCESS: any = 'TRANSACTION_REMOVE_SUCCESS';
export const TRANSACTION_REMOVE_FAILURE: any = 'TRANSACTION_REMOVE_FAILURE';

export const TRANSACTION_RESTORE: any = 'TRANSACTION_RESTORE';
export const TRANSACTION_RESTORE_QUEUE: any = 'TRANSACTION_RESTORE_QUEUE';
export const TRANSACTION_RESTORE_SUCCESS: any = 'TRANSACTION_RESTORE_SUCCESS';
export const TRANSACTION_RESTORE_FAILURE: any = 'TRANSACTION_RESTORE_FAILURE';

export const TRANSACTION_RESTORE_MANY: any = 'TRANSACTION_RESTORE_MANY';
export const TRANSACTION_REMOVE_MANY: any = 'TRANSACTION_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const TRANSACTION_SAVE_REVERT: any = 'TRANSACTION_SAVE_REVERT';
export const TRANSACTION_PENDING_CHECK: any = 'TRANSACTION_PENDING_CHECK';

/* Fetch actions */
export const fetchTransactionSuccess = (transactions: Transaction[], partial: boolean, lastFetch: number) => ({type: TRANSACTION_FETCH_SUCCESS, transactions, partial, lastFetch});
export const fetchTransactionFailure = (err: any) => {
		console.error('TransactionAction: fetchTransactionFailure', err);
		return {type: TRANSACTION_FETCH_FAILURE, err};
};
export const fetchOneTransactionQueue = (transactionId: string, sentAddedOn?: number) => ({type: TRANSACTION_FETCH_ONE_QUEUE, transactionId, sentAddedOn});
export const fetchOneTransactionSuccess = (transaction: Transaction | null, sentAddedOn?: number, transactionId: string = transaction?.id) => ({type: TRANSACTION_FETCH_ONE_SUCCESS, transactionId, transaction, sentAddedOn});
export const fetchOneTransactionFailure = (id: string, err: any) => {
		console.error('TransactionAction: fetchOneTransactionFailure', err);
		return {type: TRANSACTION_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveTransaction = (transaction: Transaction) => {
    // console.debug('⮂ TRANSACTION_SAVE action', transaction?.id);
    return {type: TRANSACTION_SAVE, transaction};
}
export const saveTransactionQueue = (transaction: Transaction, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ TRANSACTION_SAVE_QUEUE action', transaction?.id);
    return {type: TRANSACTION_SAVE_QUEUE, transaction, onQueued};
}
export const saveTransactionSuccess = (transaction: Transaction, sentAddedOn?: number) => {
    // console.debug('⮂ TRANSACTION_SAVE_SUCCESS action', transaction?.id);
    return {type: TRANSACTION_SAVE_SUCCESS, transaction, sentAddedOn};
}
export const saveTransactionFailure = (transaction: Transaction, status: number, message: string) => {
    console.warn(`⮂ TRANSACTION_SAVE_FAILURE error saving transaction: HTTP ${status}: ${message}`, transaction?.id);
    return {type: TRANSACTION_SAVE_FAILURE, transaction, status, message};
};

/* SaveMany actions */
export const saveManyTransactions = (transactions: Transaction[]) => {
    // console.debug('⮂ TRANSACTION_SAVE_MANY action', transactions?.map(transaction => transaction?.id).join(','));
    return {type: TRANSACTION_SAVE_MANY, transactions};
}
export const saveManyTransactionsQueue = (transactions: Transaction[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ TRANSACTION_SAVE_MANY_QUEUE action', transactions?.map(transaction => transaction.id).join(','));
    return {type: TRANSACTION_SAVE_MANY_QUEUE, transactions, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `transactions` (OWA-1018) */
export const saveManyTransactionsSuccess = (transactions: Transaction[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ TRANSACTION_SAVE_MANY_SUCCESS action', transactions?.map(transaction => transaction.id).join(','));
    return {type: TRANSACTION_SAVE_MANY_SUCCESS, transactions, sentAddedOns};
}
export const saveManyTransactionsFailure = (transactions: Transaction[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_TRANSACTIONS_FAILURE Error saving transactions: HTTP ${status}: ${message}`, transactions?.map(transaction => transaction.id).join(','));
		return {type: TRANSACTION_SAVE_MANY_FAILURE, transactions, status, message};
};

/* Remove actions */
export const removeTransaction = (transaction: Transaction) => ({ type: TRANSACTION_REMOVE, transaction });
export const removeTransactionQueue = (transaction: Transaction) => ({ type: TRANSACTION_REMOVE_QUEUE, transaction });
export const removeTransactionSuccess = (transaction: Transaction) => ({ type: TRANSACTION_REMOVE_SUCCESS, transaction });
export const removeTransactionFailure = (transaction: Transaction, status: number, message: string) => {
	console.error(`Redux: removeTransactionFailure Error removing transaction: HTTP ${status}: ${message}`);
	return ({ type: TRANSACTION_REMOVE_FAILURE, transaction, status, message });
};

/* Restore actions */
export const restoreTransaction = (transaction: Transaction) => ({ type: TRANSACTION_RESTORE, transaction });
export const restoreTransactionQueue = (transaction: Transaction) => ({ type: TRANSACTION_RESTORE_QUEUE, transaction });
export const restoreTransactionSuccess = (transaction: Transaction, sentAddedOn?: number) => ({ type: TRANSACTION_RESTORE_SUCCESS, transaction, sentAddedOn });
export const restoreTransactionFailure = (transaction: Transaction, status: number, message: string) => {
	console.error(`Redux: restoreTransactionFailure Error restoring transaction: HTTP ${status}: ${message}`);
	return ({ type: TRANSACTION_RESTORE_FAILURE, transaction, status, message });
};

/* RestoreMany actions */
export const restoreManyTransactions = (transactions: Transaction[]) => ({ type: TRANSACTION_RESTORE_MANY, transactions });

/* RemoveMany actions */
export const removeManyTransactions = (transactions: Transaction[]) => ({ type: TRANSACTION_REMOVE_MANY, transactions });

/* Pending save actions (OWA-1018) */
/**
 * The save `transaction` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertTransactionSave = (transaction: Transaction, options: {sentAddedOn?: number, current?: Transaction | null, keep?: boolean} = {}) =>
    ({type: TRANSACTION_SAVE_REVERT, transaction, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkTransactionPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: TRANSACTION_PENDING_CHECK, live, refused, readAt});